<template>
  <div>
    <!-- Table -->
    <PremiumTable
      title="Feedbacks"
      subtitle="Review and manage feedback submitted by society members."
      :headers="headers"
      :items="feedbacks"
      :loading="loading"
      show-search
      :search.sync="searchText"
      search-placeholder="Search feedback..."
    >
      <!-- Date -->
      <template v-slot:item.created_at="{ item }">
        {{ formatDate(item.created_at) }}
      </template>

      <!-- Reported by -->
      <template v-slot:item.member="{ item }">
        <div style="display: flex; align-items: center; gap: 12px">
          <PremiumAvatar 
            :name="item.member ? item.member.name : (item.society ? item.society.name : (item.member_id ? 'Anonymous Member' : 'Society Admin'))" 
            :image="item.member && item.member.profile_url ? (API_BASE_URL.replace('/api','') + '/uploads/' + item.member.profile_url) : (item.society && item.society.logo_url ? (API_BASE_URL.replace('/api','') + '/uploads/' + item.society.logo_url) : null)"
            :size="32" 
          />
          <div style="display: flex; flex-direction: column">
            <span style="font-weight: 600; font-size: 13.5px; color: var(--text-primary);">
              {{ item.member ? item.member.name : (item.society ? item.society.name : (item.member_id ? 'Member' : 'Society Admin')) }}
            </span>
          </div>
        </div>
      </template>

      <!-- Status badge -->
      <template v-slot:item.status="{ item }">
        <span class="ism-badge" :class="statusBadgeClass(item.status)">
          {{ statusLabel(item.status) }}
        </span>
      </template>

      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <div style="display: flex; gap: 8px; align-items: center">
          <PremiumActionButton
            icon="mdi-eye-outline"
            label="View"
            variant="info"
            @click="openView(item.id)"
          />
          <PremiumActionButton
            icon="mdi-delete-outline"
            label="Delete"
            variant="danger"
            @click="openDelete(item.id)"
          />
        </div>
      </template>
    </PremiumTable>

    <!-- Delete Confirm Modal -->
    <ConfirmModal
      :isOpen="deleteDialog"
      type="danger"
      title="Delete Feedback"
      message="Are you sure you want to delete this feedback?"
      confirmText="Yes, Delete"
      cancelText="Cancel"
      @confirm="deleteFeedback"
      @cancel="deleteDialog = false"
    />

    <!-- View Feedback Dialog -->
    <PremiumModalForm
      v-model="viewDialog"
      title="Feedback Detail"
      submitText="Update Status"
      cancelText="Close"
      :loading="false"
      max-width="520"
      @submit="updateStatus"
    >
      <div v-if="feedback">
        <!-- Fields -->
        <div style="display: flex; flex-direction: column; gap: 16px">
          <div class="ism-field-group">
            <label class="ism-field-label">Title</label>
            <div class="ism-field-value">{{ feedback.title }}</div>
          </div>
          <div class="ism-field-group">
            <label class="ism-field-label">Description</label>
            <div class="ism-field-value">{{ feedback.description }}</div>
          </div>
          <div class="ism-field-row">
            <div class="ism-field-group">
              <label class="ism-field-label">Reported By</label>
              <div class="ism-field-value">
                {{
                  feedback.member
                    ? feedback.member.name
                    : feedback.society
                    ? feedback.society.name
                    : feedback.title
                }}
              </div>
            </div>
            <div class="ism-field-group">
              <label class="ism-field-label">Date</label>
              <div class="ism-field-value">
                {{ formatDate(feedback.created_at) }}
              </div>
            </div>
          </div>

          <!-- Status Selector -->
          <div class="ism-field-group">
            <label class="ism-field-label">Status</label>
            <v-select
              v-model="feedback.status"
              :items="statusOptions"
              item-text="label"
              item-value="value"
              outlined
              dense
              hide-details
              color="#F26522"
              class="ism-status-select"
            />
          </div>

          <!-- Attachment image -->
          <div v-if="feedback.picture_url" class="ism-field-group">
            <label class="ism-field-label">Attachment</label>
            <div v-if="imgError" class="ism-img-placeholder">
              <v-icon size="32" color="var(--text-secondary)"
                >mdi-image-off-outline</v-icon
              >
              <span>Image not found</span>
            </div>
            <img
              v-else
              :src="
                API_BASE_URL.replace('/api', '') +
                '/uploads/' +
                feedback.picture_url
              "
              class="ism-feedback-img"
              @click="imgDialog = true"
              @error="imgError = true"
            />
          </div>
        </div>
      </div>
    </PremiumModalForm>

    <!-- Premium Image Lightbox -->
    <PremiumImageLightbox
      v-model="imgDialog"
      :imageSrc="feedback ? (API_BASE_URL.replace('/api', '') + '/uploads/' + feedback.picture_url) : ''"
      :title="feedback ? feedback.title : 'Feedback Image'"
    />
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import { useFilterStore } from "@/pinia/filterStore";
import PremiumTable from "../common/PremiumTable.vue";
import ConfirmModal from "../common/ConfirmModal.vue";
import PremiumModalForm from "../common/PremiumModalForm.vue";
import PremiumActionButton from "../common/PremiumActionButton.vue";
import PremiumImageLightbox from "../common/PremiumImageLightbox.vue";
import PremiumAvatar from "../common/PremiumAvatar.vue";

export default {
  name: "FeedbackPage",
  components: {
    PremiumTable,
    ConfirmModal,
    PremiumModalForm,
    PremiumActionButton,
    PremiumImageLightbox,
    PremiumAvatar,
  },

  data() {
    return {
      loading: false,
      feedbacks: [],
      feedback: null,
      deleteDialog: false,
      viewDialog: false,
      imgDialog: false,
      deletingId: null,
      imgError: false,
      searchText: "",
      API_BASE_URL:
        process.env.VUE_APP_API_URL ||
        "https://api-staging.ismartsociety.com/api",

      statusOptions: [
        { label: "Pending", value: 1 },
        { label: "Declined", value: 2 },
        { label: "Completed", value: 3 },
      ],

      headers: [
        { text: "Title", value: "title", width: "18%" },
        { text: "Description", value: "description" },
        { text: "Reported By", value: "member", width: "16%" },
        { text: "Date", value: "created_at", width: "12%" },
        { text: "Status", value: "status", width: "12%" },
        { text: "Actions", value: "actions", width: "9%", sortable: false },
      ],
    };
  },

  computed: {
    filterStore() {
      return useFilterStore();
    },
  },

  methods: {
    ...mapMutations("alert", ["setAlert"]),



    statusLabel(s) {
      return { 1: "Pending", 2: "Declined", 3: "Completed" }[s] || "—";
    },
    statusBadgeClass(s) {
      return (
        { 1: "ism-badge-amber", 2: "ism-badge-red", 3: "ism-badge-green" }[s] ||
        "ism-badge-gray"
      );
    },

    getFeedback() {
      this.loading = true;
      this.$axios
        .get("superadmin/feedback")
        .then((res) => {
          this.loading = false;
          if (res.data.status) this.feedbacks = res.data.feedback;
          else
            this.setAlert({
              showAlert: true,
              variant: "red accent-3",
              timeout: 3000,
              message: res.data.message,
            });
        })
        .catch(() => {
          this.loading = false;
        });
    },

    getInitials(name) {
      if (!name || name === "—") return "?";
      if (name === "Anonymous Member") return "AM";
      if (name === "Society Admin") return "SA";
      return name
        .split(" ")
        .filter((x) => x)
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    },

    avatarStyle(item) {
      const colors = [
        "linear-gradient(135deg, #F26522, #C94F10)",
        "linear-gradient(135deg, #3b82f6, #2563eb)",
        "linear-gradient(135deg, #10b981, #059669)",
        "linear-gradient(135deg, #8b5cf6, #7c3aed)",
        "linear-gradient(135deg, #f59e0b, #d97706)",
      ];
      const id = item.member_id || item.society_id || "default";
      const index =
        Math.abs(
          id.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0),
        ) % colors.length;
      return { background: colors[index] };
    },

    openDelete(id) {
      this.deletingId = id;
      this.deleteDialog = true;
    },

    deleteFeedback() {
      this.deleteDialog = false;
      this.$axios
        .delete("superadmin/feedback/" + this.deletingId)
        .then((res) => {
          const data = res.data;
          if (data.status) {
            this.getFeedback();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: 3000,
              message: data.message,
            });
          } else {
            this.setAlert({
              showAlert: true,
              variant: "red accent-3",
              timeout: 3000,
              message: data.message,
            });
          }
        });
    },

    openView(id) {
      this.$axios.get("superadmin/feedback/" + id).then((res) => {
        if (res.data.status) {
          this.feedback = res.data.feedback;
          this.imgError = false;
          this.viewDialog = true;
        }
      });
    },

    updateStatus() {
      this.$axios
        .put("superadmin/feedback/" + this.feedback.id, {
          status: this.feedback.status,
        })
        .then((res) => {
          const data = res.data;
          if (data.status) {
            this.viewDialog = false;
            this.getFeedback();
            this.setAlert({
              showAlert: true,
              variant: "green accent-4",
              timeout: 3000,
              message: data.message,
            });
          } else {
            this.setAlert({
              showAlert: true,
              variant: "red accent-3",
              timeout: 3000,
              message: data.message,
            });
          }
        });
    },
  },

  mounted() {
    this.getFeedback();

    // ── Restore Filters ──
    this.filterStore.updateFilter("feedback", {});
    const saved = this.filterStore.getFilter("feedback");
    if (saved && saved.search) {
      this.searchText = saved.search;
    }
  },

  watch: {
    searchText(val) {
      this.filterStore.updateFilter("feedback", { search: val });
    },
  },
};
</script>

<style scoped>
/* Fields */
.ism-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.ism-field-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.ism-field-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--text-secondary, #64748b);
}
.ism-field-value {
  font-size: 13.5px;
  color: var(--text-primary, #1e293b);
  background: var(--bg-primary, #f1f5f9);
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.07));
  border-radius: 8px;
  padding: 9px 12px;
  line-height: 1.5;
}
.ism-feedback-img {
  width: 100%;
  max-height: 200px;
  object-fit: cover;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid var(--border-color, rgba(0, 0, 0, 0.07));
  transition: opacity 0.18s;
}
.ism-feedback-img:hover {
  opacity: 0.85;
}

.ism-img-placeholder {
  width: 100%;
  height: 160px;
  background: var(--bg-primary);
  border: 1px dashed var(--border-color);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
}
</style>
